/**
 * Home's open findings, by document: one group per document (a missing
 * document is its own group), worst first, with the severity split each group
 * and the whole list carry. Reads the findings queue's own `FindingItem`s, so
 * Home and Governance count and order the same gaps.
 *
 * @module components/home/HomeFindings.logic
 */
import { severityRank as rank, type FindingItem, type Severity } from "../regulation/finding/findingItem";

/** How many open findings of each severity; `unrated` for none set. */
export type SeverityCounts = Readonly<Record<Severity | "unrated", number>>;

/** What a group's document is; `missing` for a document that does not exist yet. */
export type HomeDocumentKind = "policy" | "procedure" | "missing";

export interface HomeFindingGroup {
  readonly key: string;
  readonly documentName: string;
  readonly documentKind: HomeDocumentKind;
  /** Null when the group is the documents that do not exist yet. */
  readonly policyId: string | null;
  readonly items: ReadonlyArray<FindingItem>;
  readonly counts: SeverityCounts;
  /** It holds a high or critical finding. */
  readonly urgent: boolean;
}


export function severityCounts(items: ReadonlyArray<Pick<FindingItem, "severity">>): SeverityCounts {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, unrated: 0 };
  for (const item of items) counts[item.severity ?? "unrated"] += 1;
  return counts;
}

export const isUrgentFinding = (item: Pick<FindingItem, "severity">) =>
  item.severity === "critical" || item.severity === "high";

/** Worst first, then newest: the queue's order inside a document. */
const byPriority = (left: FindingItem, right: FindingItem) =>
  rank(right.severity) - rank(left.severity) || right.raisedAt - left.raisedAt || left.id.localeCompare(right.id);

/**
 * Open findings grouped by document. Groups come worst first (their worst
 * finding, then how many), then by name; rows inside a group by priority.
 */
export function groupOpenFindingsByDocument(
  items: ReadonlyArray<FindingItem>,
  procedureIds: ReadonlySet<string>,
): HomeFindingGroup[] {
  const groups = new Map<string, FindingItem[]>();
  for (const item of items) {
    if (item.status !== "open") continue;
    const key = item.policyId ?? `missing:${item.documentName}`;
    const list = groups.get(key);
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()]
    .map(([key, list]): HomeFindingGroup => {
      const sorted = list.toSorted(byPriority);
      const policyId = sorted[0]!.policyId;
      return {
        key,
        documentName: sorted[0]!.documentName,
        documentKind: policyId === null ? "missing" : procedureIds.has(policyId) ? "procedure" : "policy",
        policyId,
        items: sorted,
        counts: severityCounts(sorted),
        urgent: sorted.some(isUrgentFinding),
      };
    })
    .toSorted(
      (left, right) =>
        rank(right.items[0]!.severity) - rank(left.items[0]!.severity) ||
        right.items.length - left.items.length ||
        left.documentName.localeCompare(right.documentName),
    );
}

/** Whether the organization's findings have loaded, so an empty list can be told from one that failed. */
export type HomeFindingsStatus = "loading" | "unavailable" | "ready";

export function homeFindingsStatus(findings: { readonly ready: boolean; readonly unavailableReason: unknown }): HomeFindingsStatus {
  if (findings.unavailableReason !== null) return "unavailable";
  return findings.ready ? "ready" : "loading";
}
